import { AUTH_STORAGE_KEY } from "./config.js";

// Token metadata is optional: Mastodon, for example, issues non-expiring tokens.
export function tokenMetadata(token, previous = {}) {
  const lifetime = Number(token.expires_in);
  return {
    refreshToken: token.refresh_token || previous.refreshToken || null,
    expiresAt: token.expires_in != null && Number.isFinite(lifetime) && lifetime >= 0
      ? Date.now() + lifetime * 1000 : null,
    needsReconnect: false,
  };
}

function readStore() {
  try { return JSON.parse(localStorage.getItem(AUTH_STORAGE_KEY)) || {}; }
  catch { return {}; }
}

export function sessionForToken(token, apiBase) {
  if (!token) return null;
  const store = readStore();
  return store.accounts?.find((a) => a.accessToken === token
    && (apiBase === undefined || (a.apiBase || "") === (apiBase || ""))) || null;
}

function currentSession(session) {
  return readStore().accounts?.find((a) => a.id === session.id) || null;
}

// Compare the token before writing: an old request must never resurrect a
// removed account or overwrite credentials obtained by reconnecting elsewhere.
function updateSession(session, patch) {
  const store = readStore();
  const current = store.accounts?.find((a) => a.id === session.id);
  if (!current || current.accessToken !== session.accessToken) return current || null;
  Object.assign(current, patch);
  localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(store));
  globalThis.window?.dispatchEvent(new Event("chan-auth-updated"));
  return current;
}

export function requireReconnect(session) {
  if (!session || session.needsReconnect) return;
  updateSession(session, { needsReconnect: true });
}

const refreshing = new Map();

export async function refreshSession(session) {
  if (refreshing.has(session.id)) return refreshing.get(session.id);
  const run = async () => {
    const current = currentSession(session);
    if (!current) return null;
    if (current.accessToken !== session.accessToken) return current;
    if (current.needsReconnect || !current.refreshToken || !current.oauthClient) return null;
    try {
      const body = new URLSearchParams({
        grant_type: "refresh_token",
        refresh_token: current.refreshToken,
        client_id: current.oauthClient.clientId,
        client_secret: current.oauthClient.clientSecret,
      });
      const response = await fetch(`${current.apiBase || ""}/oauth/token`, {
        method: "POST", body, cache: "no-store",
      });
      if (!response.ok) {
        // Rate limits, outages and network errors retain the session for retry.
        if ([400, 401, 403].includes(response.status)) requireReconnect(current);
        return null;
      }
      const token = await response.json();
      if (!token.access_token || typeof token.access_token !== "string") return null;
      return updateSession(current, {
        accessToken: token.access_token,
        ...tokenMetadata(token, current),
      });
    } catch { return null; }
  };
  // Web Locks serialize rotating refresh tokens across tabs. The map also
  // coalesces requests in this tab on browsers without Web Locks.
  const pending = globalThis.navigator?.locks
    ? navigator.locks.request(`chan-auth-refresh:${session.id}`, run) : run();
  refreshing.set(session.id, pending);
  try { return await pending; }
  finally { refreshing.delete(session.id); }
}

export async function fetchWithSession(send, session, token, { retry = true } = {}) {
  let attemptedRefresh = false;
  let renewed = false;
  if (session) {
    const current = currentSession(session);
    if (current?.accessToken !== session.accessToken) {
      if (!current) throw new Error("Account was removed; request cancelled");
      session = current;
      token = current.accessToken;
    }
    if (session.refreshToken && session.expiresAt && session.expiresAt <= Date.now() + 30000) {
      attemptedRefresh = true;
      const fresh = await refreshSession(session);
      if (fresh) { session = fresh; token = fresh.accessToken; renewed = true; }
    }
  }
  if (session && !currentSession(session)) throw new Error("Account was removed; request cancelled");
  const response = await send(token);
  if (response.status !== 401 || !session) return response;
  if (!session.refreshToken || !session.oauthClient) {
    requireReconnect(session);
    return response;
  }
  if (attemptedRefresh) {
    if (renewed) requireReconnect(session);
    return response;
  }
  const fresh = await refreshSession(session);
  if (!fresh || !retry) return response;
  const retried = await send(fresh.accessToken);
  if (retried.status === 401) requireReconnect(fresh);
  return retried;
}
